export enum BondStatus {
  Called = 'called',
  Converted = 'converted',
  Defaulted = 'defaulted',
  Funged = 'funged',
  Liquidated = 'liquidated',
  Matured = 'matured',
  Outstanding = 'outstanding',
  PreIssuance = 'pre_issuance',
  Put = 'put',
  Repaid = 'repaid',
  Tendered = 'tendered',
  Repurchased = 'repurchased',
  Restructured = 'restructured',
  Unknown = 'unknown',
}

export enum IdentifierType {
  ISIN = 'isin',
  CUSIP = 'cusip',
}

/**
 * Broad asset class buckets the backend emits on instruments and orders.
 * Use `AssetType.Equity` etc. for value comparisons; use `AssetType` as a
 * type to constrain API response fields, function parameters, and registry
 * keys. The const object is the source of truth; the type derives from it.
 */
export const AssetType = {
  FixedIncome: 'fixed_income',
  Equity: 'equity',
  Fund: 'fund',
} as const

export type AssetType = (typeof AssetType)[keyof typeof AssetType]

const ASSET_TYPE_VALUES = new Set<string>(Object.values(AssetType))

/** Type guard: true when `type` is an asset type the backend emits. */
export function isAssetType(
  type: string | null | undefined,
): type is AssetType {
  return typeof type === 'string' && ASSET_TYPE_VALUES.has(type)
}

export enum BondType {
  Agency = 'agency',
  CD = 'cd',
  Corporate = 'corporate',
  Government = 'government',
  // Note - money market funds are not bonds, but we are modeling them as such to support new asset classes
  MoneyMarket = 'money_market',
  Municipal = 'municipal',
  Securitized = 'securitized',
  Treasury = 'treasury',
}

const BOND_TYPE_VALUES = new Set<string>(Object.values(BondType))

/** Type guard: true when `type` is a populated `BondType`. Equity lots leave this blank. */
export function isBondType(type: string | null | undefined): type is BondType {
  return typeof type === 'string' && BOND_TYPE_VALUES.has(type)
}

/**
 * Equity / fund instrument subtype emitted by the backend on equity-shaped
 * instruments. Populated for `AssetType.Equity` (Stock, DepositoryReceipt,
 * PreferredShares, Units)
 * and `AssetType.Fund` (Fund, ExchangeTradedFund); always `null` for
 * fixed_income rows.
 *
 * Use `isInstrumentClass` to narrow a raw `string | null` value (e.g. as it
 * arrives from the BE) so downstream code consumes the typed union.
 */
export const InstrumentClass = {
  Stock: 'Stock',
  DepositoryReceipt: 'DepositoryReceipt',
  PreferredShares: 'PreferredShares',
  Units: 'Units',
  Fund: 'Fund',
  ExchangeTradedFund: 'ExchangeTradedFund',
  ExchangeTradedCommodity: 'ExchangeTradedCommodity',
} as const

export type InstrumentClass =
  (typeof InstrumentClass)[keyof typeof InstrumentClass]

const INSTRUMENT_CLASS_VALUES = new Set<string>(Object.values(InstrumentClass))

export function isInstrumentClass(
  value: string | null | undefined,
): value is InstrumentClass {
  if (!value) {
    return false
  }
  return INSTRUMENT_CLASS_VALUES.has(value)
}

export enum CallFrequency {
  Annually = 'annually',
  Complex = 'complex',
  Daily = 'daily',
  Every10Years = 'every_10_years',
  Every2Years = 'every_2_years',
  Every3Years = 'every_3_years',
  Every4Years = 'every_4_years',
  Every5Years = 'every_5_years',
  Every6Years = 'every_6_years',
  Every7Days = 'every_7_days',
  Every7Years = 'every_7_years',
  Interest = 'interest',
  Mandatory = 'mandatory',
  Monthly = 'monthly',
  OneTime = 'one_time',
  Quarterly = 'quarterly',
  SemiAnnually = 'semi_annually',
}

export enum CallType {
  Ordinary = 'ordinary',
  MakeWhole = 'make_whole',
  Regulatory = 'regulatory',
  Special = 'special',
}

/**
 * Country Domicile enum representing ISO3 country codes.
 *
 * **IMPORTANT - Backend API Contract:**
 * - This enum represents ISO country codes (e.g., 'US', 'AU', 'FR')
 * - These values are sent to/from the backend API
 * - For display names, use {@link COUNTRY_LABELS}
 *
 * **Usage:**
 * ```tsx
 * // API request/response
 * const bond: InstrumentApiResponse = {
 *   country_domicile: CountryDomicile.US  // 'US'
 * }
 *
 * // Display to user
 * import { COUNTRY_LABELS } from '@/utils/presentation/bonds'
 * const displayName = COUNTRY_LABELS[bond.country_domicile]  // 'United States'
 * ```
 */
export enum CountryDomicile {
  AU = 'AU',
  NL = 'NL',
  DE = 'DE',
  FR = 'FR',
  CH = 'CH',
  IT = 'IT',
  JP = 'JP',
  AT = 'AT',
  BE = 'BE',
  DK = 'DK',
  ES = 'ES',
  GR = 'GR',
  IE = 'IE',
  LU = 'LU',
  NO = 'NO',
  PT = 'PT',
  SE = 'SE',
  AR = 'AR',
  BR = 'BR',
  CL = 'CL',
  MX = 'MX',
  PE = 'PE',
  VE = 'VE',
  NZ = 'NZ',
  HK = 'HK',
  SG = 'SG',
  TH = 'TH',
  ID = 'ID',
  TW = 'TW',
  KR = 'KR',
  PH = 'PH',
  CZ = 'CZ',
  PL = 'PL',
  HU = 'HU',
  RU = 'RU',
  TR = 'TR',
  CN = 'CN',
  IN = 'IN',
  PK = 'PK',
  LK = 'LK',
  IL = 'IL',
  EG = 'EG',
  MA = 'MA',
  ZA = 'ZA',
  JM = 'JM',
  CO = 'CO',
  GB = 'GB',
  FI = 'FI',
  EC = 'EC',
  MY = 'MY',
  BH = 'BH',
  SK = 'SK',
  JO = 'JO',
  EE = 'EE',
  CY = 'CY',
  LT = 'LT',
  LV = 'LV',
  MT = 'MT',
  HR = 'HR',
  GH = 'GH',
  TN = 'TN',
  NG = 'NG',
  LB = 'LB',
  QA = 'QA',
  OM = 'OM',
  BD = 'BD',
  KZ = 'KZ',
  TT = 'TT',
  UA = 'UA',
  KW = 'KW',
  BG = 'BG',
  BM = 'BM',
  AE = 'AE',
  VN = 'VN',
  AF = 'AF',
  AX = 'AX',
  AL = 'AL',
  DZ = 'DZ',
  AS = 'AS',
  AD = 'AD',
  AO = 'AO',
  AI = 'AI',
  AQ = 'AQ',
  AG = 'AG',
  AM = 'AM',
  AW = 'AW',
  AZ = 'AZ',
  BS = 'BS',
  BB = 'BB',
  BY = 'BY',
  BZ = 'BZ',
  BJ = 'BJ',
  BT = 'BT',
  BO = 'BO',
  BQ = 'BQ',
  BA = 'BA',
  BW = 'BW',
  BV = 'BV',
  IO = 'IO',
  BN = 'BN',
  BF = 'BF',
  BI = 'BI',
  KH = 'KH',
  CM = 'CM',
  CA = 'CA',
  CV = 'CV',
  KY = 'KY',
  CF = 'CF',
  TD = 'TD',
  CX = 'CX',
  CC = 'CC',
  CD = 'CD',
  CG = 'CG',
  CK = 'CK',
  CR = 'CR',
  CI = 'CI',
  CU = 'CU',
  CW = 'CW',
  DJ = 'DJ',
  DM = 'DM',
  DO = 'DO',
  SV = 'SV',
  GQ = 'GQ',
  ER = 'ER',
  ET = 'ET',
  FK = 'FK',
  FO = 'FO',
  FJ = 'FJ',
  PF = 'PF',
  TF = 'TF',
  GA = 'GA',
  GM = 'GM',
  GE = 'GE',
  GI = 'GI',
  GL = 'GL',
  GD = 'GD',
  GP = 'GP',
  GU = 'GU',
  GT = 'GT',
  GG = 'GG',
  GN = 'GN',
  GW = 'GW',
  GY = 'GY',
  HT = 'HT',
  HM = 'HM',
  VA = 'VA',
  HN = 'HN',
  IS = 'IS',
  IR = 'IR',
  IQ = 'IQ',
  IM = 'IM',
  JE = 'JE',
  KE = 'KE',
  KI = 'KI',
  KP = 'KP',
  KG = 'KG',
  LA = 'LA',
  LS = 'LS',
  LR = 'LR',
  LY = 'LY',
  LI = 'LI',
  MO = 'MO',
  MK = 'MK',
  MG = 'MG',
  MW = 'MW',
  MV = 'MV',
  ML = 'ML',
  MH = 'MH',
  MQ = 'MQ',
  MR = 'MR',
  MU = 'MU',
  YT = 'YT',
  FM = 'FM',
  MD = 'MD',
  MC = 'MC',
  MN = 'MN',
  ME = 'ME',
  MS = 'MS',
  MZ = 'MZ',
  MM = 'MM',
  NA = 'NA',
  NR = 'NR',
  NP = 'NP',
  AN = 'AN',
  NC = 'NC',
  NI = 'NI',
  NE = 'NE',
  NU = 'NU',
  NF = 'NF',
  MP = 'MP',
  PW = 'PW',
  PS = 'PS',
  PA = 'PA',
  PG = 'PG',
  PY = 'PY',
  PN = 'PN',
  PR = 'PR',
  RE = 'RE',
  RO = 'RO',
  RW = 'RW',
  BL = 'BL',
  SH = 'SH',
  KN = 'KN',
  LC = 'LC',
  MF = 'MF',
  SX = 'SX',
  PM = 'PM',
  VC = 'VC',
  WS = 'WS',
  SM = 'SM',
  ST = 'ST',
  SA = 'SA',
  SN = 'SN',
  RS = 'RS',
  SC = 'SC',
  SL = 'SL',
  SI = 'SI',
  SB = 'SB',
  SO = 'SO',
  GS = 'GS',
  SS = 'SS',
  SD = 'SD',
  SR = 'SR',
  SJ = 'SJ',
  SZ = 'SZ',
  SY = 'SY',
  TJ = 'TJ',
  GF = 'GF',
  TZ = 'TZ',
  TL = 'TL',
  TG = 'TG',
  TK = 'TK',
  TO = 'TO',
  TM = 'TM',
  TC = 'TC',
  TV = 'TV',
  UG = 'UG',
  UM = 'UM',
  US = 'US',
  UY = 'UY',
  VU = 'VU',
  VG = 'VG',
  VI = 'VI',
  WF = 'WF',
  EH = 'EH',
  YE = 'YE',
  ZM = 'ZM',
  ZW = 'ZW',
  AC = 'AC',
  CP = 'CP',
  DG = 'DG',
  TA = 'TA',
  KM = 'KM',
  UZ = 'UZ',
  Z1 = 'Z1',
  Z2 = 'Z2',
  Z3 = 'Z3',
  Z4 = 'Z4',
  EU = 'EU',
  XB = 'XB',
  XS = 'XS',
  Z5 = 'Z5',
}

export enum CountryIssue {
  CA = 'CA',
  EU = 'EU',
  LU = 'LU',
  PR = 'PR',
  US = 'US',
}

export type CountryOfRisk =
  | 'AE'
  | 'AL'
  | 'AM'
  | 'AO'
  | 'AR'
  | 'AT'
  | 'AU'
  | 'AW'
  | 'AZ'
  | 'BB'
  | 'BE'
  | 'BH'
  | 'BJ'
  | 'BM'
  | 'BO'
  | 'BR'
  | 'BS'
  | 'BY'
  | 'BZ'
  | 'CA'
  | 'CD'
  | 'CH'
  | 'CI'
  | 'CL'
  | 'CM'
  | 'CN'
  | 'CO'
  | 'CR'
  | 'CY'
  | 'CZ'
  | 'DE'
  | 'DK'
  | 'DO'
  | 'EC'
  | 'EG'
  | 'ES'
  | 'ET'
  | 'FI'
  | 'FR'
  | 'GA'
  | 'GB'
  | 'GD'
  | 'GE'
  | 'GH'
  | 'GR'
  | 'GT'
  | 'GU'
  | 'GY'
  | 'HK'
  | 'HN'
  | 'HU'
  | 'ID'
  | 'IE'
  | 'IL'
  | 'IM'
  | 'IN'
  | 'IS'
  | 'IT'
  | 'JE'
  | 'JM'
  | 'JO'
  | 'JP'
  | 'KE'
  | 'KG'
  | 'KN'
  | 'KR'
  | 'KW'
  | 'KY'
  | 'KZ'
  | 'LA'
  | 'LB'
  | 'LK'
  | 'LR'
  | 'LT'
  | 'LU'
  | 'LV'
  | 'MA'
  | 'MC'
  | 'MD'
  | 'ME'
  | 'MH'
  | 'ML'
  | 'MN'
  | 'MO'
  | 'MT'
  | 'MU'
  | 'MV'
  | 'MX'
  | 'MY'
  | 'NA'
  | 'NG'
  | 'NL'
  | 'NO'
  | 'NZ'
  | 'OM'
  | 'PA'
  | 'PE'
  | 'PG'
  | 'PH'
  | 'PK'
  | 'PL'
  | 'PR'
  | 'PT'
  | 'PY'
  | 'QA'
  | 'RO'
  | 'RS'
  | 'RU'
  | 'RW'
  | 'SA'
  | 'SE'
  | 'SG'
  | 'SI'
  | 'SN'
  | 'SR'
  | 'SS'
  | 'SV'
  | 'TG'
  | 'TH'
  | 'TJ'
  | 'TN'
  | 'TR'
  | 'TT'
  | 'TW'
  | 'UA'
  | 'US'
  | 'UY'
  | 'UZ'
  | 'VE'
  | 'VG'
  | 'VI'
  | 'VN'
  | 'XB'
  | 'ZA'
  | 'ZM'
  | 'ZW'

export enum CouponFrequency {
  Annual = 'annual',
  SemiAnnual = 'semi_annual',
  Quarterly = 'quarterly',
  Monthly = 'monthly',
  Zero = 'zero',
  AtMaturity = 'at_maturity',
}

export enum CouponType {
  Fixed = 'fixed',
  Floating = 'floating',
  Variable = 'variable',
  Zero = 'zero',
  Complex = 'complex',
}

export enum CouponSubtype {
  AdjustableRate = 'Adjustable rate',
  DeferredInterest = 'Deferred interest',
  FixedRate = 'Fixed rate',
  FixedRateUnconfirmed = 'Fixed rate - unconfirmed',
  FlexibleRate = 'Flexible rate',
  FloatingAuctionRate = 'Floating auction rate',
  FloatingRate = 'Floating rate',
  IndexLinked = 'Index Linked',
  InverseFloating = 'Inverse floating',
  LinkedInverseFloater = 'Linked inverse floater',
  OriginalIssueDiscount = 'Original issue discount',
  ReserveCusip = 'Reserve CUSIP',
  ShortTermDiscount = 'Short term discount',
  SteppedCoupon = 'Stepped coupon',
  StrippedConvertible = 'Stripped convertible',
  StrippedCoupon = 'Stripped coupon',
  StrippedCouponPayment = 'Stripped coupon payment',
  StrippedCouponPrincipal = 'Stripped coupon principal',
  StrippedPrincipal = 'Stripped principal',
  StrippedTaxCredit = 'Stripped tax credit',
  TaxCredit = 'Tax credit',
  TaxCreditOid = 'Tax credit OID',
  Unknown = 'Unknown',
  VariableRate = 'Variable rate',
  ZeroCoupon = 'Zero coupon',
}

/**
 * Currency enum representing ISO 4217 currency codes.
 *
 * **IMPORTANT - Backend API Contract:**
 * - This enum represents ISO 4217 currency codes (e.g., 'USD', 'EUR', 'GBP')
 * - These values are sent to/from the backend API
 * - For display names, use {@link CURRENCY_LABELS} from `shared/constants/reference-data-labels.ts`
 *
 * **Usage:**
 * ```tsx
 * // API request/response
 * const bond: InstrumentApiResponse = {
 *   currency: Currency.USD  // 'USD'
 * }
 *
 * // Display to user
 * import { CURRENCY_LABELS } from '@/shared/constants/reference-data-labels'
 * const displayName = CURRENCY_LABELS[bond.currency]  // 'United States dollar'
 * ```
 */
export enum Currency {
  AED = 'AED',
  AFN = 'AFN',
  ALL = 'ALL',
  AMD = 'AMD',
  ANG = 'ANG',
  AOA = 'AOA',
  ARS = 'ARS',
  AUD = 'AUD',
  AWG = 'AWG',
  AZN = 'AZN',
  BAM = 'BAM',
  BBD = 'BBD',
  BDT = 'BDT',
  BGN = 'BGN',
  BHD = 'BHD',
  BIF = 'BIF',
  BMD = 'BMD',
  BND = 'BND',
  BOB = 'BOB',
  BOV = 'BOV',
  BRL = 'BRL',
  BSD = 'BSD',
  BTN = 'BTN',
  BWP = 'BWP',
  BYR = 'BYR',
  BZD = 'BZD',
  CAD = 'CAD',
  CDF = 'CDF',
  CHE = 'CHE',
  CHF = 'CHF',
  CHW = 'CHW',
  CLF = 'CLF',
  CLP = 'CLP',
  CNY = 'CNY',
  COP = 'COP',
  COU = 'COU',
  CRC = 'CRC',
  CUC = 'CUC',
  CUP = 'CUP',
  CVE = 'CVE',
  CZK = 'CZK',
  DJF = 'DJF',
  DKK = 'DKK',
  DOP = 'DOP',
  DZD = 'DZD',
  EEK = 'EEK',
  EGP = 'EGP',
  ERN = 'ERN',
  ETB = 'ETB',
  EUR = 'EUR',
  FJD = 'FJD',
  FKP = 'FKP',
  GBP = 'GBP',
  GEL = 'GEL',
  GHS = 'GHS',
  GIP = 'GIP',
  GMD = 'GMD',
  GNF = 'GNF',
  GTQ = 'GTQ',
  GYD = 'GYD',
  HKD = 'HKD',
  HNL = 'HNL',
  HRK = 'HRK',
  HTG = 'HTG',
  HUF = 'HUF',
  IDR = 'IDR',
  ILS = 'ILS',
  INR = 'INR',
  IQD = 'IQD',
  IRR = 'IRR',
  ISK = 'ISK',
  JMD = 'JMD',
  JOD = 'JOD',
  JPY = 'JPY',
  KES = 'KES',
  KGS = 'KGS',
  KHR = 'KHR',
  KMF = 'KMF',
  KPW = 'KPW',
  KRW = 'KRW',
  KWD = 'KWD',
  KYD = 'KYD',
  KZT = 'KZT',
  LAK = 'LAK',
  LBP = 'LBP',
  LKR = 'LKR',
  LRD = 'LRD',
  LSL = 'LSL',
  LTL = 'LTL',
  LVL = 'LVL',
  LYD = 'LYD',
  MAD = 'MAD',
  MDL = 'MDL',
  MGA = 'MGA',
  MKD = 'MKD',
  MMK = 'MMK',
  MNT = 'MNT',
  MOP = 'MOP',
  MRO = 'MRO',
  MUR = 'MUR',
  MVR = 'MVR',
  MWK = 'MWK',
  MXN = 'MXN',
  MXV = 'MXV',
  MYR = 'MYR',
  MZN = 'MZN',
  NAD = 'NAD',
  NGN = 'NGN',
  NIO = 'NIO',
  NOK = 'NOK',
  NPR = 'NPR',
  NZD = 'NZD',
  OMR = 'OMR',
  PAB = 'PAB',
  PEN = 'PEN',
  PGK = 'PGK',
  PHP = 'PHP',
  PKR = 'PKR',
  PLN = 'PLN',
  PYG = 'PYG',
  QAR = 'QAR',
  RON = 'RON',
  RSD = 'RSD',
  RUB = 'RUB',
  RWF = 'RWF',
  SAR = 'SAR',
  SBD = 'SBD',
  SCR = 'SCR',
  SDG = 'SDG',
  SEK = 'SEK',
  SGD = 'SGD',
  SHP = 'SHP',
  SLL = 'SLL',
  SOS = 'SOS',
  SRD = 'SRD',
  STD = 'STD',
  SYP = 'SYP',
  SZL = 'SZL',
  THB = 'THB',
  TJS = 'TJS',
  TMT = 'TMT',
  TND = 'TND',
  TOP = 'TOP',
  TRY = 'TRY',
  TTD = 'TTD',
  TWD = 'TWD',
  TZS = 'TZS',
  UAH = 'UAH',
  UGX = 'UGX',
  USD = 'USD',
  USN = 'USN',
  USS = 'USS',
  UYU = 'UYU',
  UZS = 'UZS',
  VEF = 'VEF',
  VND = 'VND',
  VUV = 'VUV',
  WST = 'WST',
  XAF = 'XAF',
  XAG = 'XAG',
  XAU = 'XAU',
  XBA = 'XBA',
  XBB = 'XBB',
  XBC = 'XBC',
  XBD = 'XBD',
  XCD = 'XCD',
  XDR = 'XDR',
  XFU = 'XFU',
  XOF = 'XOF',
  XPD = 'XPD',
  XPF = 'XPF',
  XPT = 'XPT',
  YER = 'YER',
  ZAR = 'ZAR',
  ZMK = 'ZMK',
  ZMW = 'ZMW',
  ZWL = 'ZWL',
  ADF = 'ADF',
  ADP = 'ADP',
  ATS = 'ATS',
  BEF = 'BEF',
  CYP = 'CYP',
  DEM = 'DEM',
  ESP = 'ESP',
  FIM = 'FIM',
  FRF = 'FRF',
  GRD = 'GRD',
  IEP = 'IEP',
  ITL = 'ITL',
  LUF = 'LUF',
  MCF = 'MCF',
  MAF = 'MAF',
  MTL = 'MTL',
  NLG = 'NLG',
  PTE = 'PTE',
  SIT = 'SIT',
  SKK = 'SKK',
  SML = 'SML',
  VAL = 'VAL',
  XEU = 'XEU',
  AFA = 'AFA',
  AON = 'AON',
  AOR = 'AOR',
  ARL = 'ARL',
  ARP = 'ARP',
  ARA = 'ARA',
  AZM = 'AZM',
  BGL = 'BGL',
  BOP = 'BOP',
  BRB = 'BRB',
  BRC = 'BRC',
  BRE = 'BRE',
  BRN = 'BRN',
  BRR = 'BRR',
  CLE = 'CLE',
  CSD = 'CSD',
  CSK = 'CSK',
  DDM = 'DDM',
  ECS = 'ECS',
  ECV = 'ECV',
  GQE = 'GQE',
  ESA = 'ESA',
  ESB = 'ESB',
  GNE = 'GNE',
  GHC = 'GHC',
  GWP = 'GWP',
  ILP = 'ILP',
  ILR = 'ILR',
  ISJ = 'ISJ',
  LAJ = 'LAJ',
  MGF = 'MGF',
  MKN = 'MKN',
  MLF = 'MLF',
  MVQ = 'MVQ',
  MXP = 'MXP',
  MZM = 'MZM',
  NFD = 'NFD',
  PEH = 'PEH',
  PEI = 'PEI',
  PLZ = 'PLZ',
  ROL = 'ROL',
  RUR = 'RUR',
  SDD = 'SDD',
  SDP = 'SDP',
  SRG = 'SRG',
  SUR = 'SUR',
  SVC = 'SVC',
  TJR = 'TJR',
  TMM = 'TMM',
  TRL = 'TRL',
  UAK = 'UAK',
  UGS = 'UGS',
  UYN = 'UYN',
  VEB = 'VEB',
  XFO = 'XFO',
  YDD = 'YDD',
  YUD = 'YUD',
  YUN = 'YUN',
  YUR = 'YUR',
  YUO = 'YUO',
  YUG = 'YUG',
  YUM = 'YUM',
  ZAL = 'ZAL',
  ZRN = 'ZRN',
  ZRZ = 'ZRZ',
  ZWC = 'ZWC',
  ZWD = 'ZWD',
  ZWN = 'ZWN',
  ZWR = 'ZWR',
  WCU = 'WCU',
  XCU = 'XCU',
  CAX = 'CAX',
  CNH = 'CNH',
  EUX = 'EUX',
  JPX = 'JPX',
  TRX = 'TRX',
  GBX = 'GBX',
  USX = 'USX',
  ZWX = 'ZWX',
  SSP = 'SSP',
  UYI = 'UYI',
  QZZ = 'QZZ',
  KWF = 'KWF',
  NAC = 'NAC',
  BWT = 'BWT',
  ZAC = 'ZAC',
  ILA = 'ILA',
  XBT = 'XBT',
  BCF = 'BCF',
  VES = 'VES',
  UYW = 'UYW',
}

const CURRENCY_VALUES = new Set<string>(Object.values(Currency))

export function isCurrency(
  value: string | null | undefined,
): value is Currency {
  return value != null && CURRENCY_VALUES.has(value)
}

export enum DayCount {
  A360 = 'A/360',
  A365 = 'A/365',
  Thirty360 = '30/360',
  Thirty365 = '30/365',
  AA = 'A/A',
  ThirtyE360 = '30E/360',
  B252 = 'B/252',
  A364 = 'A/364',
}

export enum MunicipalTaxStatus {
  TaxExempt = 'Tax Exempt',
  Taxable = 'Taxable',
  AlternativeMinimumTax = 'Alternative Minimum Tax',
}

export enum MunicipalSector {
  Refunded = 'Refunded',
  GoLocal = 'GO - Local',
  GoState = 'GO - State',
  RevenueAirport = 'Revenue - Airport',
  RevenueEducation = 'Revenue - Education',
  RevenueHealth = 'Revenue - Health',
  RevenueHospitals = 'Revenue - Hospitals',
  RevenueIndustrialDevelopmentRevenue = 'Revenue - Industrial Development Revenue',
  RevenueLeasingCopsAndAppropriations = 'Revenue - Leasing COPS & Appropriations',
  RevenueMisc = 'Revenue - Misc',
  RevenueMultiFamilyHousing = 'Revenue - Multi-Family Housing',
  RevenuePollutionControl = 'Revenue - Pollution Control',
  RevenuePower = 'Revenue - Power',
  RevenueSingleFamilyHousing = 'Revenue - Single Family Housing',
  RevenueTax = 'Revenue - Tax',
  RevenueTobacco = 'Revenue - Tobacco',
  RevenueTollAndTurnpike = 'Revenue - Toll & Turnpike',
  RevenueTransportation = 'Revenue - Transportation',
  RevenueUtilitiesOther = 'Revenue - Utilities - Other',
  RevenueWaterAndSewer = 'Revenue - Water & Sewer',
}

export enum MunicipalType {
  GeneralObligation = 'general_obligation',
  Revenue = 'revenue',
}

export enum NaicsSector {
  AccommodationAndFoodServices = 'Accommodation and Food Services',
  AdministrativeAndSupportAndWasteManagementAndRemediationServices = 'Administrative and Support and Waste Management and Remediation Services',
  AgricultureForestryFishingAndHunting = 'Agriculture, Forestry, Fishing and Hunting',
  ArtsEntertainmentAndRecreation = 'Arts, Entertainment, and Recreation',
  Construction = 'Construction',
  EducationalServices = 'Educational Services',
  FinanceAndInsurance = 'Finance and Insurance',
  HealthCareAndSocialAssistance = 'Health Care and Social Assistance',
  Information = 'Information',
  ManagementOfCompaniesAndEnterprises = 'Management of Companies and Enterprises',
  Manufacturing = 'Manufacturing',
  MiningQuarryingAndOilAndGasExtraction = 'Mining, Quarrying, and Oil and Gas Extraction',
  OtherServicesExceptPublicAdministration = 'Other Services (except Public Administration)',
  ProfessionalScientificAndTechnicalServices = 'Professional, Scientific, and Technical Services',
  PublicAdministration = 'Public Administration',
  RealEstateAndRentalAndLeasing = 'Real Estate and Rental and Leasing',
  RetailTrade = 'Retail Trade',
  TransportationAndWarehousing = 'Transportation and Warehousing',
  Utilities = 'Utilities',
  WholesaleTrade = 'Wholesale Trade',
}

export enum NaicsSubsector {
  Accommodation = 'Accommodation',
  AdministrationOfEnvironmentalQualityPrograms = 'Administration of Environmental Quality Programs',
  AdministrativeAndSupportServices = 'Administrative and Support Services',
  AirTransportation = 'Air Transportation',
  AmbulatoryHealthCareServices = 'Ambulatory Health Care Services',
  AmusementGamblingAndRecreationIndustries = 'Amusement, Gambling, and Recreation Industries',
  AnimalProductionAndAquaculture = 'Animal Production and Aquaculture',
  ApparelManufacturing = 'Apparel Manufacturing',
  BeverageAndTobaccoProductManufacturing = 'Beverage and Tobacco Product Manufacturing',
  BroadcastingAndContentProviders = 'Broadcasting and Content Providers',
  BuildingMaterialAndGardenEquipmentAndSuppliesDealers = 'Building Material and Garden Equipment and Supplies Dealers',
  ChemicalManufacturing = 'Chemical Manufacturing',
  ClothingClothingAccessoriesShoeAndJewelryRetailers = 'Clothing, Clothing Accessories, Shoe, and Jewelry Retailers',
  ComputerAndElectronicProductManufacturing = 'Computer and Electronic Product Manufacturing',
  ComputingInfrastructureProvidersDataProcessingWebHostingAndRelatedServices = 'Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services',
  ConstructionOfBuildings = 'Construction of Buildings',
  CouriersAndMessengers = 'Couriers and Messengers',
  CreditIntermediationAndRelatedActivities = 'Credit Intermediation and Related Activities',
  CropProduction = 'Crop Production',
  EducationalServices = 'Educational Services',
  ElectricalEquipmentApplianceAndComponentManufacturing = 'Electrical Equipment, Appliance, and Component Manufacturing',
  ExecutiveLegislativeAndOtherGeneralGovernmentSupport = 'Executive, Legislative, and Other General Government Support',
  FabricatedMetalProductManufacturing = 'Fabricated Metal Product Manufacturing',
  FishingHuntingAndTrapping = 'Fishing, Hunting and Trapping',
  FoodAndBeverageRetailers = 'Food and Beverage Retailers',
  FoodManufacturing = 'Food Manufacturing',
  FoodServicesAndDrinkingPlaces = 'Food Services and Drinking Places',
  ForestryAndLogging = 'Forestry and Logging',
  FundsTrustsAndOtherFinancialVehicles = 'Funds, Trusts, and Other Financial Vehicles',
  FurnitureAndRelatedProductManufacturing = 'Furniture and Related Product Manufacturing',
  FurnitureHomeFurnishingsElectronicsAndApplianceRetailers = 'Furniture, Home Furnishings, Electronics, and Appliance Retailers',
  GasolineStationsAndFuelDealers = 'Gasoline Stations and Fuel Dealers',
  GeneralMerchandiseRetailers = 'General Merchandise Retailers',
  HealthAndPersonalCareRetailers = 'Health and Personal Care Retailers',
  HeavyAndCivilEngineeringConstruction = 'Heavy and Civil Engineering Construction',
  Hospitals = 'Hospitals',
  InsuranceCarriersAndRelatedActivities = 'Insurance Carriers and Related Activities',
  LessorsOfNonfinancialIntangibleAssetsExceptCopyrightedWorks = 'Lessors of Nonfinancial Intangible Assets (except Copyrighted Works)',
  MachineryManufacturing = 'Machinery Manufacturing',
  ManagementOfCompaniesAndEnterprises = 'Management of Companies and Enterprises',
  MerchantWholesalersDurableGoods = 'Merchant Wholesalers, Durable Goods',
  MerchantWholesalersNondurableGoods = 'Merchant Wholesalers, Nondurable Goods',
  MiningExceptOilAndGas = 'Mining (except Oil and Gas)',
  MiscellaneousManufacturing = 'Miscellaneous Manufacturing',
  MonetaryAuthoritiesCentralBank = 'Monetary Authorities-Central Bank',
  MotionPictureAndSoundRecordingIndustries = 'Motion Picture and Sound Recording Industries',
  MotorVehicleAndPartsDealers = 'Motor Vehicle and Parts Dealers',
  MuseumsHistoricalSitesAndSimilarInstitutions = 'Museums, Historical Sites, and Similar Institutions',
  NationalSecurityAndInternationalAffairs = 'National Security and International Affairs',
  NonmetallicMineralProductManufacturing = 'Nonmetallic Mineral Product Manufacturing',
  NursingAndResidentialCareFacilities = 'Nursing and Residential Care Facilities',
  OilAndGasExtraction = 'Oil and Gas Extraction',
  PaperManufacturing = 'Paper Manufacturing',
  PerformingArtsSpectatorSportsAndRelatedIndustries = 'Performing Arts, Spectator Sports, and Related Industries',
  PersonalAndLaundryServices = 'Personal and Laundry Services',
  PetroleumAndCoalProductsManufacturing = 'Petroleum and Coal Products Manufacturing',
  PipelineTransportation = 'Pipeline Transportation',
  PlasticsAndRubberProductsManufacturing = 'Plastics and Rubber Products Manufacturing',
  PrimaryMetalManufacturing = 'Primary Metal Manufacturing',
  PrintingAndRelatedSupportActivities = 'Printing and Related Support Activities',
  PrivateHouseholds = 'Private Households',
  ProfessionalScientificAndTechnicalServices = 'Professional, Scientific, and Technical Services',
  PublishingIndustries = 'Publishing Industries',
  RailTransportation = 'Rail Transportation',
  RealEstate = 'Real Estate',
  ReligiousGrantmakingCivicProfessionalAndSimilarOrganizations = 'Religious, Grantmaking, Civic, Professional, and Similar Organizations',
  RentalAndLeasingServices = 'Rental and Leasing Services',
  RepairAndMaintenance = 'Repair and Maintenance',
  ScenicAndSightseeingTransportation = 'Scenic and Sightseeing Transportation',
  SecuritiesCommodityContractsAndOtherFinancialInvestmentsAndRelatedActivities = 'Securities, Commodity Contracts, and Other Financial Investments and Related Activities',
  SpecialtyTradeContractors = 'Specialty Trade Contractors',
  SportingGoodsHobbyMusicalInstrumentBookAndMiscellaneousRetailers = 'Sporting Goods, Hobby, Musical Instrument, Book, and Miscellaneous Retailers',
  SupportActivitiesForAgricultureAndForestry = 'Support Activities for Agriculture and Forestry',
  SupportActivitiesForMining = 'Support Activities for Mining',
  SupportActivitiesForTransportation = 'Support Activities for Transportation',
  Telecommunications = 'Telecommunications',
  TextileMills = 'Textile Mills',
  TransitAndGroundPassengerTransportation = 'Transit and Ground Passenger Transportation',
  TransportationEquipmentManufacturing = 'Transportation Equipment Manufacturing',
  TruckTransportation = 'Truck Transportation',
  Utilities = 'Utilities',
  WarehousingAndStorage = 'Warehousing and Storage',
  WasteManagementAndRemediationServices = 'Waste Management and Remediation Services',
  WaterTransportation = 'Water Transportation',
  WebSearchPortalsLibrariesArchivesAndOtherInformationServices = 'Web Search Portals, Libraries, Archives, and Other Information Services',
  WoodProductManufacturing = 'Wood Product Manufacturing',
}

export enum Purpose {
  AccessToEssentialServices = 'Access to Essential Services',
  AcquiringAndDistributionOfVaccine = 'Acquiring and distribution of vaccine',
  Acquisition = 'Acquisition',
  AffordableBasicInfrastructure = 'Affordable Basic Infrastructure',
  Agriculture = 'Agriculture',
  AircraftShipFinance = 'Aircraft & Ship finance',
  Airlines = 'Airlines',
  Airports = 'Airports',
  AlternativeEnergy = 'Alternative Energy',
  AquaticBiodiversityConservation = 'Aquatic Biodiversity Conservation',
  Bridges = 'Bridges',
  BudgetaryPurpose = 'Budgetary Purpose',
  CapitalExpenditure = 'Capital expenditure',
  CapitalExpenditureFinancingExpenses = 'Capital expenditure/Financing expenses',
  CarbonReductionThroughReforestationAndAvoidedDeforestation = 'Carbon reduction through reforestation and avoided deforestation',
  CertainFacilitiesCourts = 'Certain Facilities/Courts',
  CharterSchools = 'Charter Schools',
  ChinaUrbanConstruction = 'China Urban Construction',
  CircularEconomyAdaptedEcoEfficientProductsProductionTechnologiesProcesses = 'Circular Economy Adapted/Eco-efficient Products, Production Technologies/Processes',
  CivicConventionCenters = 'Civic/Convention Centers',
  CleanTransport = 'Clean Transport',
  ClimateChangeAdaptation = 'Climate Change Adaptation',
  CombinedUtilities = 'Combined Utilities',
  CommercialPaperBackup = 'Commercial paper backup',
  CorrectionalFacilitiesJails = 'Correctional Facilities/Jails',
  CreditEnhancement = 'Credit Enhancement',
  DividendRecapitalization = 'Dividend Recapitalization',
  DividendOrDistributionToShareholders = 'Dividend or Distribution to Shareholders',
  EEducationProgramsEducationProjects = 'E-education programs - Education Projects',
  EconomicDevelopment = 'Economic Development',
  ElectricPublicPower = 'Electric & Public Power',
  EligibleGreenProjects = 'Eligible Green Projects',
  EligibleSocialExpenditures = 'Eligible Social Expenditures',
  EmployeeStockOwnershipPlan = 'Employee stock ownership plan',
  EmploymentGenerationAlleviateUnemployment = 'Employment Generation/Alleviate Unemployment',
  EnergyEfficiency = 'Energy Efficiency',
  EnvironmentalProtectionProjects = 'Environmental Protection Projects',
  EquipmentUpgradeConstruction = 'Equipment Upgrade/Construction',
  FacilitatingTheDevelopmentOfNewPharmaceuticalProducts = 'Facilitating the development of new pharmaceutical products',
  FinancingOfSubordinatedLoan = 'Financing of Subordinated Loan',
  FireStationEquipment = 'Fire Station/Equipment',
  FloodControlStormDrainage = 'Flood Control Storm Drainage',
  FundingNewTechnologiesToReduceGhsEmissions = 'Funding new technologies to reduce GHS emissions',
  Gas = 'Gas',
  GenderEqualityInHumanDevelopment = 'Gender Equality in Human Development',
  GeneralPurpose = 'General Purpose',
  GeneralPurposeAcquisition = 'General Purpose/Acquisition',
  GeneralPurposeEquipmentUpgrade = 'General Purpose/Equipment Upgrade',
  GeneralPurposePublicImprovements = 'General Purpose/Public Improvements',
  GeneralPurposeRefinance = 'General Purpose/Refinance',
  GeneralPurposeStockRepurchase = 'General Purpose/Stock Repurchase',
  GeneralPurposeWorkingCapital = 'General Purpose/Working Capital',
  GovernmentPublicBuildings = 'Government/Public Buildings',
  GreenConstructionBuildings = 'Green Construction/Buildings',
  Guarantee = 'Guarantee',
  HigherEducation = 'Higher Education',
  HospitalEquipmentLoans = 'Hospital Equipment Loans',
  Hospitals = 'Hospitals',
  IndustrialDevelopment = 'Industrial Development',
  Infrastructure = 'Infrastructure',
  Irrigation = 'Irrigation',
  LandPreservation = 'Land Preservation',
  LeaseFinancing = 'Lease financing',
  LibrariesOrMuseums = 'Libraries or Museums',
  LifecareRetirementCenters = 'Lifecare/Retirement Centers',
  MallsShoppingCenters = 'Malls/Shopping Centers',
  MassRapidTransit = 'Mass/Rapid Transit',
  Merger = 'Merger',
  MergerOrAcquisition = 'Merger or Acquisition',
  MultiFamilyHousing = 'Multi-Family Housing',
  NewPublicHousing = 'New Public Housing',
  NursingHomes = 'Nursing Homes',
  OfficeBuildingLtdPartn = 'Office Building/Ltd Partn',
  OtherEducation = 'Other Education',
  OtherHealthCare = 'Other Health Care',
  OtherHousing = 'Other Housing',
  OtherPublicService = 'Other Public Service',
  OtherRecreation = 'Other Recreation',
  OtherTransportation = 'Other Transportation',
  OtherUtilities = 'Other Utilities',
  Pandemic = 'Pandemic',
  ParkingFacilities = 'Parking Facilities',
  ParksZoosBeaches = 'Parks/Zoos/Beaches',
  PensionFundingRetirement = 'Pension Funding/Retirement',
  PoliceStationEquipment = 'Police Station/Equipment',
  PollutionControl = 'Pollution Control',
  PollutionPreventionControl = 'Pollution Prevention & Control',
  PrimarySecondaryEducation = 'Primary/Secondary Education',
  PrivatePlacementSecurities = 'Private Placement Securities',
  PrivateSchools = 'Private Schools',
  ProductionSupplyOfCannabis = 'Production/Supply of Cannabis',
  ProjectFinance = 'Project Finance',
  PropertyExpenditAcquisitDevelopment = 'Property Expendit (acquisit/development)',
  PurchaseOfChargedAssets = 'Purchase of Charged Assets',
  PurchaseOfFundingAgreement = 'Purchase of Funding Agreement',
  RealEstateLoan = 'Real estate loan',
  Recapitalization = 'Recapitalization',
  ReceivablesProgram = 'Receivables Program',
  RedeemExistingBondsOrSecurities = 'Redeem Existing Bonds or Securities',
  RedevelopLandClearance = 'Redevelop/Land Clearance',
  RefinanceExitFromBankruptcy = 'Refinance Exit from Bankruptcy',
  RefinanceFinancingExpenses = 'Refinance/Financing expenses',
  RenewableEnergyProjects = 'Renewable Energy Projects',
  RepayBankLoanOrBridgeFinancing = 'Repay Bank Loan or Bridge Financing',
  RepayIntercompanyDebt = 'Repay Intercompany Debt',
  Restructuring = 'Restructuring',
  Sanitation = 'Sanitation',
  SeaportsMarineTerminals = 'Seaports/Marine Terminals',
  SingleFamilyHousing = 'Single Family Housing',
  SingleMultiFamilyHousing = 'Single/Multi-Family Housing',
  SocialHousingAffordableHousing = 'Social Housing/Affordable Housing',
  SocioeconomicAdvancementAndEmpowerment = 'Socioeconomic Advancement And Empowerment',
  SolidWasteResourceRecovery = 'Solid Waste/Resource Recovery',
  Spinoff = 'Spinoff',
  StadiumSportsComplex = 'Stadium/Sports Complex',
  StockRepurchase = 'Stock Repurchase',
  StudentHousing = 'Student Housing',
  StudentLoans = 'Student Loans',
  SustainableDevelopmentProjects = 'Sustainable Development Projects',
  SustainableForestry = 'Sustainable Forestry',
  SustainableWaterOrWastewaterManagement = 'Sustainable Water or Wastewater management',
  Telephone = 'Telephone',
  TenderOffer = 'Tender Offer',
  Theaters = 'Theaters',
  TollRoadsStreetsHighways = 'Toll Roads/Streets/Highways',
  Tunnels = 'Tunnels',
  Undisclosed = 'Undisclosed',
  Veterans = 'Veterans',
  WaterSewer = 'Water & Sewer',
  WorkingCapital = 'Working capital',
}

export enum SecurityCode {
  CigaretteTax = 'Cigarette Tax',
  DoubleBarreled = 'Double Barreled',
  FederalGrant = 'Federal Grant',
  FuelVehicleTax = 'Fuel/Vehicle Tax',
  HotelTax = 'Hotel Tax',
  LeaseRental = 'Lease-Rental',
  LimitedGO = 'Limited G.O.',
  MiscellaneousTax = 'Miscellaneous Tax',
  PersonalIncomeTax = 'Personal Income Tax',
  Pilot = 'Pilot',
  Revenue = 'Revenue',
  SalesExciseTax = 'Sales/Excise Tax',
  SpecialAssessment = 'Special Assessment',
  TaxAllocationIncrement = 'Tax Allocation/Increment',
  TobaccoSettlementNonAppropriated = 'Tobacco Settlement Non-Appropriated',
  TobaccoStateAppropriated = 'Tobacco State Appropriated',
  UnlimitedGO = 'Unlimited G.O.',
  USGovernmentBacked = 'U.S. Government-Backed',
}

export enum Sector {
  GoLocal = 'GO - Local',
  GoState = 'GO - State',
  Refunded = 'Refunded',
  RevenueAirport = 'Revenue - Airport',
  RevenueEducation = 'Revenue - Education',
  RevenueHealth = 'Revenue - Health',
  RevenueHospitals = 'Revenue - Hospitals',
  RevenueIndustrialDevelopmentRevenue = 'Revenue - Industrial Development Revenue',
  RevenueLeasingCopsAndAppropriations = 'Revenue - Leasing COPS & Appropriations',
  RevenueMisc = 'Revenue - Misc',
  RevenueMultiFamilyHousing = 'Revenue - Multi-Family Housing',
  RevenuePollutionControl = 'Revenue - Pollution Control',
  RevenuePower = 'Revenue - Power',
  RevenueSingleFamilyHousing = 'Revenue - Single Family Housing',
  RevenueTax = 'Revenue - Tax',
  RevenueTobacco = 'Revenue - Tobacco',
  RevenueTollAndTurnpike = 'Revenue - Toll & Turnpike',
  RevenueTransportation = 'Revenue - Transportation',
  RevenueUtilitiesOther = 'Revenue - Utilities - Other',
  RevenueWaterAndSewer = 'Revenue - Water & Sewer',
  Agency = 'Agency',
  AssetBacked = 'Asset Backed',
  Automotive = 'Automotive',
  Banking = 'Banking',
  BasicIndustry = 'Basic Industry',
  CapitalGoods = 'Capital Goods',
  CollateralizedMortgageObligation = 'Collateralized Mortgage Obligation',
  CommercialMortgageBacked = 'Commercial Mortgage Backed',
  ConsumerGoods = 'Consumer Goods',
  CoveredBonds = 'Covered Bonds',
  Energy = 'Energy',
  FinancialServices = 'Financial Services',
  ForeignSovereign = 'Foreign Sovereign',
  GovernmentGuaranteed = 'Government Guaranteed',
  Healthcare = 'Healthcare',
  Insurance = 'Insurance',
  Leisure = 'Leisure',
  LocalAuthority = 'Local-Authority',
  Media = 'Media',
  MortgageBacked = 'Mortgage Backed',
  RealEstate = 'Real Estate',
  Retail = 'Retail',
  Services = 'Services',
  Sovereign = 'Sovereign',
  Supranational = 'Supranational',
  TechnologyAndElectronics = 'Technology & Electronics',
  Telecommunications = 'Telecommunications',
  Transportation = 'Transportation',
  Utility = 'Utility',
}

export enum Seniority {
  Senior = 'Senior',
  Subordinated = 'Subordinated',
  JuniorSubordinated = 'Junior Subordinated',
  Secured = 'Secured',
  UpperTier2 = 'Upper Tier 2',
  Tier1 = 'Tier 1',
  SecuritizedSenior = 'Securitized Senior',
  SecuritizedSubordinated = 'Securitized Subordinated',
  SecuritizedMezzanine = 'Securitized Mezzanine',
  AdditionalTier1 = 'Additional Tier 1',
  Tier2 = 'Tier 2',
  PreferredStock = 'Preferred Stock',
  SeniorNonPreferred = 'Senior Non Preferred',
  JuniorLien = 'Junior Lien',
  JuniorSubordinateLien = 'Junior Subordinate Lien',
  SeniorLien = 'Senior Lien',
  SeniorSubordinateLien = 'Senior Subordinate Lien',
  SubordinateLien = 'Subordinate Lien',
}

export enum SettlementConvention {
  TPlus1 = 'T+1',
  TPlus2 = 'T+2',
}

export enum SpCreditWatch {
  Positive = 'positive',
  Negative = 'negative',
  Developing = 'developing',
  NotMeaningful = 'not_meaningful',
}

export enum SpOutlook {
  Positive = 'positive',
  Negative = 'negative',
  Developing = 'developing',
  Stable = 'stable',
  NotRated = 'not_rated',
  NotMeaningful = 'not_meaningful',
}

export enum SPRating {
  AAA = 'AAA',
  AA_PLUS = 'AA+',
  AA = 'AA',
  AA_MINUS = 'AA-',
  A_PLUS = 'A+',
  A = 'A',
  A_MINUS = 'A-',
  BBB_PLUS = 'BBB+',
  BBB = 'BBB',
  BBB_MINUS = 'BBB-',
  BB_PLUS = 'BB+',
  BB = 'BB',
  BB_MINUS = 'BB-',
  B_PLUS = 'B+',
  B = 'B',
  B_MINUS = 'B-',
  CCC_PLUS = 'CCC+',
  CCC = 'CCC',
  CCC_MINUS = 'CCC-',
  CC = 'CC',
  C = 'C',
  D = 'D',
  NR = 'NR',
}

export enum FitchRating {
  AAA = 'AAA',
  AA_PLUS = 'AA+',
  AA = 'AA',
  AA_MINUS = 'AA-',
  A_PLUS = 'A+',
  A = 'A',
  A_MINUS = 'A-',
  BBB_PLUS = 'BBB+',
  BBB = 'BBB',
  BBB_MINUS = 'BBB-',
  BB_PLUS = 'BB+',
  BB = 'BB',
  BB_MINUS = 'BB-',
  B_PLUS = 'B+',
  B = 'B',
  B_MINUS = 'B-',
  CCC_PLUS = 'CCC+',
  CCC = 'CCC',
  CCC_MINUS = 'CCC-',
  CC = 'CC',
  C = 'C',
  D = 'D',
  NR = 'NR',
}

export enum MoodysRating {
  Aaa = 'Aaa',
  Aa1 = 'Aa1',
  Aa2 = 'Aa2',
  Aa3 = 'Aa3',
  A1 = 'A1',
  A2 = 'A2',
  A3 = 'A3',
  Baa1 = 'Baa1',
  Baa2 = 'Baa2',
  Baa3 = 'Baa3',
  Ba1 = 'Ba1',
  Ba2 = 'Ba2',
  Ba3 = 'Ba3',
  B1 = 'B1',
  B2 = 'B2',
  B3 = 'B3',
  Caa1 = 'Caa1',
  Caa2 = 'Caa2',
  Caa3 = 'Caa3',
  Ca = 'Ca',
  C = 'C',
}

export enum TreasurySubtype {
  Bill = 'bill',
  Bond = 'bond',
  Note = 'note',
  Strips = 'strips',
  Tips = 'tips',
  Floating = 'floating',
}

export enum UseOfProceeds {
  Agriculture = 'Agriculture',
  Airports = 'Airports',
  Bridges = 'Bridges',
  CharterSchool = 'Charter School',
  CivicConventionCenters = 'Civic/Convention Centers',
  CmoBackedHousing = 'CMO-Backed Housing',
  Cogeneration = 'Cogeneration',
  CombinedUtilities = 'Combined Utilities',
  CorrectionFacilitiesCourts = 'Correction Facilities/Courts',
  EconomicDevelopment = 'Economic Development',
  ElectricAndPublicPower = 'Electric & Public Power',
  FireStationEquipment = 'Fire Station/Equipment',
  FloodControlStormDrain = 'Flood Control/Storm Drain',
  Gas = 'Gas',
  GeneralPurposePublicImpt = 'General Purpose/Public Impt',
  GovernmentPublicBuildings = 'Government/Public Buildings',
  HigherEducation = 'Higher Education',
  HospitalEquipmentLoans = 'Hospital Equipment Loans',
  Hospitals = 'Hospitals',
  Hotel = 'Hotel',
  IndustrialDevelopment = 'Industrial Development',
  Irrigation = 'Irrigation',
  LandPreservation = 'Land Preservation',
  LibrariesOrMuseums = 'Libraries or Museums',
  LifecareRetirementCenters = 'Lifecare/Retirement Centers',
  MallsShoppingCenters = 'Malls/Shopping Centers',
  MassRapidTransit = 'Mass/Rapid Transit',
  MilitaryHousing = 'Military Housing',
  MobileHome = 'Mobile Home',
  MultiFamilyHousing = 'Multi-Family Housing',
  NewPublicHousing = 'New Public Housing',
  NursingHomes = 'Nursing Homes',
  OfficeBuildingLimitedPartnerships = 'Office Building/Limited Partnerships',
  OtherEducation = 'Other Education',
  OtherHealthCare = 'Other Health Care',
  OtherHousing = 'Other Housing',
  OtherIndustrialDevelopment = 'Other Industrial Development',
  OtherPostEmployeeBenefits = 'Other Post Employee Benefits',
  OtherPublicService = 'Other Public Service',
  OtherRecreation = 'Other Recreation',
  OtherTransportation = 'Other Transportation',
  OtherUtilities = 'Other Utilities',
  ParkingFacilities = 'Parking Facilities',
  ParksZoosBeaches = 'Parks/Zoos/Beaches',
  PensionObligation = 'Pension Obligation',
  PoliceStationEquipment = 'Police Station/Equipment',
  PollutionControl = 'Pollution Control',
  PrimarySecondaryEducation = 'Primary/Secondary Education',
  PrivateHigherEducation = 'Private Higher Education',
  PrivateHumanServiceProvider = 'Private Human Service Provider',
  PublicHigherEducation = 'Public Higher Education',
  PublicHumanServiceProvider = 'Public Human Service Provider',
  RedevelopmentLandClearance = 'Redevelopment/Land Clearance',
  Sanitation = 'Sanitation',
  SeaportsMarineTerminals = 'Seaports/Marine Terminals',
  SecuredAirlines = 'Secured Airlines',
  SeniorHousingIndependentLiving = 'Senior Housing Independent Living',
  Sewer = 'Sewer',
  SingleFamilyHousing = 'Single-Family Housing',
  SingleMultiFamilyHousing = 'Single/Multi-Family Housing',
  SolidWasteResRecovery = 'Solid Waste/Res Recovery',
  StadiumsSportsComplex = 'Stadiums/Sports Complex',
  StreetsHighways = 'Streets/Highways',
  StudentHousing = 'Student Housing',
  StudentLoans = 'Student Loans',
  Telephone = 'Telephone',
  Theaters = 'Theaters',
  TollRoad = 'Toll Road',
  Tunnels = 'Tunnels',
  UnsecuredAirlines = 'Unsecured Airlines',
  Veterans = 'Veterans',
  Water = 'Water',
  WaterAndSewer = 'Water & Sewer',
}

export enum CallNotificationConvention {
  Calendar = 'calendar',
}

export enum IceSector1 {
  Convertible = 'Convertible',
  Corporate = 'Corporate',
  PreferredSecurities = 'Preferred Securities',
  QuasiAndForeignGovernment = 'Quasi & Foreign Government',
  SecuritizedCollateralized = 'Securitized/Collateralized',
  Sovereign = 'Sovereign',
  USTaxExemptMunicipals = 'U.S. Tax-Exempt Municipals',
}

export enum IceSector2 {
  Securitized = 'Securitized',
  Industrials = 'Industrials',
  QuasiAndForeignGovernment = 'Quasi & Foreign Government',
  PfdUtility = 'Pfd-Utility',
  Revenue = 'Revenue',
  Refunded = 'Refunded',
  Financial = 'Financial',
  GO = 'GO',
  PfdIndustrials = 'Pfd-Industrials',
  Utility = 'Utility',
  Sovereign = 'Sovereign',
  CvtIndustrials = 'Cvt-Industrials',
  CvtFinancial = 'Cvt-Financial',
  Covered = 'Covered',
  CvtUtility = 'Cvt-Utility',
  PfdFinancial = 'Pfd-Financial',
}

export enum IceSector3 {
  Agency = 'Agency',
  AssetBacked = 'Asset Backed',
  Automotive = 'Automotive',
  Banking = 'Banking',
  BasicIndustry = 'Basic Industry',
  CapitalGoods = 'Capital Goods',
  CommercialMortgageBacked = 'Commercial Mortgage Backed',
  ConsumerGoods = 'Consumer Goods',
  CoveredBonds = 'Covered Bonds',
  CvtAutomotive = 'Cvt-Automotive',
  CvtBanking = 'Cvt-Banking',
  CvtBasicIndustry = 'Cvt-Basic Industry',
  CvtCapitalGoods = 'Cvt-Capital Goods',
  CvtConsumerGoods = 'Cvt-Consumer Goods',
  CvtEnergy = 'Cvt-Energy',
  CvtFinancialServices = 'Cvt-Financial Services',
  CvtHealthcare = 'Cvt-Healthcare',
  CvtInsurance = 'Cvt-Insurance',
  CvtLeisure = 'Cvt-Leisure',
  CvtMedia = 'Cvt-Media',
  CvtRealEstate = 'Cvt-Real Estate',
  CvtRetail = 'Cvt-Retail',
  CvtServices = 'Cvt-Services',
  CvtTechnologyAndElectronics = 'Cvt-Technology & Electronics',
  CvtTelecommunications = 'Cvt-Telecommunications',
  CvtTransportation = 'Cvt-Transportation',
  CvtUtility = 'Cvt-Utility',
  Energy = 'Energy',
  FinancialServices = 'Financial Services',
  ForeignSovereign = 'Foreign Sovereign',
  GOLocal = 'GO - Local',
  GOState = 'GO - State',
  GovernmentGuaranteed = 'Government Guaranteed',
  Healthcare = 'Healthcare',
  Insurance = 'Insurance',
  Leisure = 'Leisure',
  LocalAuthority = 'Local-Authority',
  Media = 'Media',
  PfdAutomotive = 'Pfd-Automotive',
  PfdBanking = 'Pfd-Banking',
  PfdConsumerGoods = 'Pfd-Consumer Goods',
  PfdEnergy = 'Pfd-Energy',
  PfdFinancialServices = 'Pfd-Financial Services',
  PfdHealthcare = 'Pfd-Healthcare',
  PfdInsurance = 'Pfd-Insurance',
  PfdMedia = 'Pfd-Media',
  PfdRealEstate = 'Pfd-Real Estate',
  PfdTechnologyAndElectronics = 'Pfd-Technology & Electronics',
  PfdUtility = 'Pfd-Utility',
  RealEstate = 'Real Estate',
  Refunded = 'Refunded',
  Retail = 'Retail',
  RevenueAirport = 'Revenue - Airport',
  RevenueEducation = 'Revenue - Education',
  RevenueHealth = 'Revenue - Health',
  RevenueHospitals = 'Revenue - Hospitals',
  RevenueIndustrialDevelopmentRevenue = 'Revenue - Industrial Development Revenue',
  RevenueLeasingCOPSAndAppropriations = 'Revenue - Leasing COPS & Appropriations',
  RevenueMisc = 'Revenue - Misc',
  RevenueMultiFamilyHousing = 'Revenue - Multi-Family Housing',
  RevenuePollutionControl = 'Revenue - Pollution Control',
  RevenuePower = 'Revenue - Power',
  RevenueSingleFamilyHousing = 'Revenue - Single Family Housing',
  RevenueTax = 'Revenue - Tax',
  RevenueTobacco = 'Revenue - Tobacco',
  RevenueTollAndTurnpike = 'Revenue - Toll & Turnpike',
  RevenueTransportation = 'Revenue - Transportation',
  RevenueUtilitiesOther = 'Revenue - Utilities - Other',
  RevenueWaterAndSewer = 'Revenue - Water & Sewer',
  Services = 'Services',
  Sovereign = 'Sovereign',
  Supranational = 'Supranational',
  TechnologyAndElectronics = 'Technology & Electronics',
  Telecommunications = 'Telecommunications',
  Transportation = 'Transportation',
  USTaxableMunicipal = 'U.S. Taxable Municipal',
  Utility = 'Utility',
}

export enum IceSector4 {
  ABSMiscellaneousABS = 'ABS Miscellaneous ABS',
  ABSUtilities = 'ABS Utilities',
  Advertising = 'Advertising',
  AerospaceDefense = 'Aerospace/Defense',
  Agency = 'Agency',
  AirTransportation = 'Air Transportation',
  AutoLoans = 'Auto Loans',
  AutoPartsAndEquipment = 'Auto Parts & Equipment',
  Automakers = 'Automakers',
  Banking = 'Banking',
  Beverage = 'Beverage',
  Brokerage = 'Brokerage',
  BuildingAndConstruction = 'Building & Construction',
  BuildingMaterials = 'Building Materials',
  CableAndSatelliteTV = 'Cable & Satellite TV',
  Chemicals = 'Chemicals',
  ConsCommLeaseFinancing = 'Cons/Comm/Lease Financing',
  CvtAdvertising = 'Cvt-Advertising',
  CvtAerospaceDefense = 'Cvt-Aerospace/Defense',
  CvtAirTransportation = 'Cvt-Air Transportation',
  CvtAutoPartsAndEquipment = 'Cvt-Auto Parts & Equipment',
  CvtAutomakers = 'Cvt-Automakers',
  CvtBanking = 'Cvt-Banking',
  CvtBeverage = 'Cvt-Beverage',
  CvtBrokerage = 'Cvt-Brokerage',
  CvtBuildingAndConstruction = 'Cvt-Building & Construction',
  CvtBuildingMaterials = 'Cvt-Building Materials',
  CvtCableAndSatelliteTV = 'Cvt-Cable & Satellite TV',
  CvtChemicals = 'Cvt-Chemicals',
  CvtConsCommLeaseFinancing = 'Cvt-Cons/Comm/Lease Financing',
  CvtDepartmentStores = 'Cvt-Department Stores',
  CvtDiversifiedCapitalGoods = 'Cvt-Diversified Capital Goods',
  CvtElectricDistrTrans = 'Cvt-Electric-Distr/Trans',
  CvtElectricGeneration = 'Cvt-Electric-Generation',
  CvtElectricIntegrated = 'Cvt-Electric-Integrated',
  CvtElectronics = 'Cvt-Electronics',
  CvtEnergyExplorationAndProduction = 'Cvt-Energy - Exploration & Production',
  CvtEnvironmental = 'Cvt-Environmental',
  CvtFoodAndDrugRetailers = 'Cvt-Food & Drug Retailers',
  CvtFoodWholesale = 'Cvt-Food - Wholesale',
  CvtForestryPaper = 'Cvt-Forestry/Paper',
  CvtGaming = 'Cvt-Gaming',
  CvtGasDistribution = 'Cvt-Gas Distribution',
  CvtHealthFacilities = 'Cvt-Health Facilities',
  CvtHealthServices = 'Cvt-Health Services',
  CvtHotels = 'Cvt-Hotels',
  CvtHousingAssociation = 'Cvt-Housing Association',
  CvtInsuranceBrokerage = 'Cvt-Insurance Brokerage',
  CvtIntegratedEnergy = 'Cvt-Integrated Energy',
  CvtInvestmentsAndMiscFinancialServices = 'Cvt-Investments & Misc Financial Services',
  CvtLifeInsurance = 'Cvt-Life Insurance',
  CvtMachinery = 'Cvt-Machinery',
  CvtManagedCare = 'Cvt-Managed Care',
  CvtMediaContent = 'Cvt-Media Content',
  CvtMediaDiversified = 'Cvt-Media - Diversified',
  CvtMedicalProducts = 'Cvt-Medical Products',
  CvtMetalsMiningExcludingSteel = 'Cvt-Metals/Mining Excluding Steel',
  CvtMonolineInsurance = 'Cvt-Monoline Insurance',
  CvtMultiLineInsurance = 'Cvt-Multi-Line Insurance',
  CvtNonElectricUtilities = 'Cvt-Non-Electric Utilities',
  CvtOilFieldEquipmentAndServices = 'Cvt-Oil Field Equipment & Services',
  CvtOilRefiningAndMarketing = 'Cvt-Oil Refining & Marketing',
  CvtPAndC = 'Cvt-P&C',
  CvtPackaging = 'Cvt-Packaging',
  CvtPersonalAndHouseholdProducts = 'Cvt-Personal & Household Products',
  CvtPharmaceuticals = 'Cvt-Pharmaceuticals',
  CvtPrintingAndPublishing = 'Cvt-Printing & Publishing',
  CvtREITs = 'Cvt-REITs',
  CvtRail = 'Cvt-Rail',
  CvtRealEstateDevAndMgt = 'Cvt-RealEstate Dev & Mgt',
  CvtRecreationAndTravel = 'Cvt-Recreation & Travel',
  CvtReinsurance = 'Cvt-Reinsurance',
  CvtRestaurants = 'Cvt-Restaurants',
  CvtSoftwareServices = 'Cvt-Software/Services',
  CvtSpecialtyRetail = 'Cvt-Specialty Retail',
  CvtSteelProducersProducts = 'Cvt-Steel Producers/Products',
  CvtSupportServices = 'Cvt-Support-Services',
  CvtTechHardwareAndEquipment = 'Cvt-Tech Hardware & Equipment',
  CvtTelecomSatellite = 'Cvt-Telecom - Satellite',
  CvtTelecomWireless = 'Cvt-Telecom - Wireless',
  CvtTelecomWirelineIntegratedAndServices = 'Cvt-Telecom - Wireline Integrated & Services',
  CvtTheatersAndEntertainment = 'Cvt-Theaters & Entertainment',
  CvtTobacco = 'Cvt-Tobacco',
  CvtTransportInfrastructureServices = 'Cvt-Transport Infrastructure/Services',
  CvtTruckingAndDelivery = 'Cvt-Trucking & Delivery',
  DepartmentStores = 'Department Stores',
  DiscountStores = 'Discount Stores',
  DiversifiedCapitalGoods = 'Diversified Capital Goods',
  ETM = 'ETM',
  ElectricDistrTrans = 'Electric-Distr/Trans',
  ElectricGeneration = 'Electric-Generation',
  ElectricIntegrated = 'Electric-Integrated',
  Electronics = 'Electronics',
  EnergyExplorationAndProduction = 'Energy - Exploration & Production',
  Environmental = 'Environmental',
  FoodAndDrugRetailers = 'Food & Drug Retailers',
  FoodWholesale = 'Food - Wholesale',
  ForeignSovereign = 'Foreign Sovereign',
  ForestryPaper = 'Forestry/Paper',
  GOLocal = 'GO - Local',
  GOState = 'GO - State',
  Gaming = 'Gaming',
  GasDistribution = 'Gas Distribution',
  GovernmentGuaranteed = 'Government Guaranteed',
  HealthFacilities = 'Health Facilities',
  HealthServices = 'Health Services',
  Hotels = 'Hotels',
  HousingAssociation = 'Housing Association',
  InsuranceBrokerage = 'Insurance Brokerage',
  IntegratedEnergy = 'Integrated Energy',
  InvestmentsAndMiscFinancialServices = 'Investments & Misc Financial Services',
  LifeInsurance = 'Life Insurance',
  LocalAuthority = 'Local-Authority',
  Machinery = 'Machinery',
  ManagedCare = 'Managed Care',
  MediaContent = 'Media Content',
  MediaDiversified = 'Media - Diversified',
  MedicalProducts = 'Medical Products',
  MetalsMiningExcludingSteel = 'Metals/Mining Excluding Steel',
  MonolineInsurance = 'Monoline Insurance',
  MortgageCoveredBonds = 'Mortgage Covered Bonds',
  MultiLineInsurance = 'Multi-Line Insurance',
  NonElectricUtilities = 'Non-Electric Utilities',
  OilFieldEquipmentAndServices = 'Oil Field Equipment & Services',
  OilRefiningAndMarketing = 'Oil Refining & Marketing',
  OtherCoveredBonds = 'Other Covered Bonds',
  PAndC = 'P&C',
  Packaging = 'Packaging',
  PersonalAndHouseholdProducts = 'Personal & Household Products',
  PfdAutoLoans = 'Pfd-Auto Loans',
  PfdBanking = 'Pfd-Banking',
  PfdBrokerage = 'Pfd-Brokerage',
  PfdConsCommLeaseFinancing = 'Pfd-Cons/Comm/Lease Financing',
  PfdElectricDistrTrans = 'Pfd-Electric-Distr/Trans',
  PfdElectricGeneration = 'Pfd-Electric-Generation',
  PfdElectricIntegrated = 'Pfd-Electric-Integrated',
  PfdFoodWholesale = 'Pfd-Food - Wholesale',
  PfdGasDistribution = 'Pfd-Gas Distribution',
  PfdHealthFacilities = 'Pfd-Health Facilities',
  PfdInvestmentsAndMiscFinancialServices = 'Pfd-Investments & Misc Financial Services',
  PfdLifeInsurance = 'Pfd-Life Insurance',
  PfdOilRefiningAndMarketing = 'Pfd-Oil Refining & Marketing',
  PfdPrintingAndPublishing = 'Pfd-Printing & Publishing',
  PfdREITs = 'Pfd-REITs',
  PfdSoftwareServices = 'Pfd-Software/Services',
  Pharmaceuticals = 'Pharmaceuticals',
  PreRefunded = 'Pre-Refunded',
  PrintingAndPublishing = 'Printing & Publishing',
  PublicLoanCoveredBonds = 'Public Loan Covered Bonds',
  REITs = 'REITs',
  Rail = 'Rail',
  RealEstateDevAndMgt = 'RealEstate Dev & Mgt',
  RecreationAndTravel = 'Recreation & Travel',
  Reinsurance = 'Reinsurance',
  Restaurants = 'Restaurants',
  RevenueAirport = 'Revenue - Airport',
  RevenueEducation = 'Revenue - Education',
  RevenueHealth = 'Revenue - Health',
  RevenueHospitals = 'Revenue - Hospitals',
  RevenueIndustrialDevelopmentRevenue = 'Revenue - Industrial Development Revenue',
  RevenueLeasingCOPSAndAppropriations = 'Revenue - Leasing COPS & Appropriations',
  RevenueMisc = 'Revenue - Misc',
  RevenueMultiFamilyHousing = 'Revenue - Multi-Family Housing',
  RevenuePollutionControl = 'Revenue - Pollution Control',
  RevenuePower = 'Revenue - Power',
  RevenueSingleFamilyHousing = 'Revenue - Single Family Housing',
  RevenueTax = 'Revenue - Tax',
  RevenueTobacco = 'Revenue - Tobacco',
  RevenueTollAndTurnpike = 'Revenue - Toll & Turnpike',
  RevenueTransportation = 'Revenue - Transportation',
  RevenueUtilitiesOther = 'Revenue - Utilities - Other',
  RevenueWaterAndSewer = 'Revenue - Water & Sewer',
  SoftwareServices = 'Software/Services',
  Sovereign = 'Sovereign',
  SpecialtyRetail = 'Specialty Retail',
  SteelProducersProducts = 'Steel Producers/Products',
  SupportServices = 'Support-Services',
  Supranational = 'Supranational',
  TaxableETM = 'Taxable ETM',
  TaxableGOLocal = 'Taxable GO - Local',
  TaxableGOState = 'Taxable GO - State',
  TaxableRevenueAirport = 'Taxable Revenue - Airport',
  TaxableRevenueEducation = 'Taxable Revenue - Education',
  TaxableRevenueHealth = 'Taxable Revenue - Health',
  TaxableRevenueHospitals = 'Taxable Revenue - Hospitals',
  TaxableRevenueIndustrialDevelopmentRevenue = 'Taxable Revenue - Industrial Development Revenue',
  TaxableRevenueLeasingCOPSAndAppropriations = 'Taxable Revenue - Leasing COPS & Appropriations',
  TaxableRevenueMisc = 'Taxable Revenue - Misc',
  TaxableRevenueMultiFamilyHousing = 'Taxable Revenue - Multi-Family Housing',
  TaxableRevenuePollutionControl = 'Taxable Revenue - Pollution Control',
  TaxableRevenuePower = 'Taxable Revenue - Power',
  TaxableRevenueSingleFamilyHousing = 'Taxable Revenue - Single Family Housing',
  TaxableRevenueTax = 'Taxable Revenue - Tax',
  TaxableRevenueTobacco = 'Taxable Revenue - Tobacco',
  TaxableRevenueTollAndTurnpike = 'Taxable Revenue - Toll & Turnpike',
  TaxableRevenueTransportation = 'Taxable Revenue - Transportation',
  TaxableRevenueUtilitiesOther = 'Taxable Revenue - Utilities - Other',
  TaxableRevenueWaterAndSewer = 'Taxable Revenue - Water & Sewer',
  TechHardwareAndEquipment = 'Tech Hardware & Equipment',
  TelecomSatellite = 'Telecom - Satellite',
  TelecomWireless = 'Telecom - Wireless',
  TelecomWirelineIntegratedAndServices = 'Telecom - Wireline Integrated & Services',
  TheatersAndEntertainment = 'Theaters & Entertainment',
  Tobacco = 'Tobacco',
  TransportInfrastructureServices = 'Transport Infrastructure/Services',
  TruckingAndDelivery = 'Trucking & Delivery',
}

// GICS sector hierarchy from Axioma reference data: L1 Sector, L2 Industry
// Group, L3 Industry. Sourced from `axioma_reference_data` parquet.
// Mapped to the `gics_sector_1` / `gics_sector_2` / `gics_sector_3` instrument
// fields respectively.
export enum GicsSector {
  CommunicationServices = 'Communication Services',
  ConsumerDiscretionary = 'Consumer Discretionary',
  ConsumerStaples = 'Consumer Staples',
  Energy = 'Energy',
  Financials = 'Financials',
  HealthCare = 'Health Care',
  Industrials = 'Industrials',
  InformationTechnology = 'Information Technology',
  Materials = 'Materials',
  RealEstate = 'Real Estate',
  Utilities = 'Utilities',
}

export enum GicsIndustryGroup {
  AutomobilesAndComponents = 'Automobiles & Components',
  Banks = 'Banks',
  CapitalGoods = 'Capital Goods',
  CommercialAndProfessionalServices = 'Commercial & Professional Services',
  ConsumerDiscretionaryDistributionAndRetail = 'Consumer Discretionary Distribution & Retail',
  ConsumerDurablesAndApparel = 'Consumer Durables & Apparel',
  ConsumerServices = 'Consumer Services',
  ConsumerStaplesDistributionAndRetail = 'Consumer Staples Distribution & Retail',
  Energy = 'Energy',
  EquityRealEstateInvestmentTrusts = 'Equity Real Estate Investment Trusts (REITs)',
  FinancialServices = 'Financial Services',
  FoodBeverageAndTobacco = 'Food, Beverage & Tobacco',
  HealthCareEquipmentAndServices = 'Health Care Equipment & Services',
  HouseholdAndPersonalProducts = 'Household & Personal Products',
  Insurance = 'Insurance',
  Materials = 'Materials',
  MediaAndEntertainment = 'Media & Entertainment',
  PharmaceuticalsBiotechnologyAndLifeSciences = 'Pharmaceuticals, Biotechnology & Life Sciences',
  RealEstateManagementAndDevelopment = 'Real Estate Management & Development',
  SemiconductorsAndSemiconductorEquipment = 'Semiconductors & Semiconductor Equipment',
  SoftwareAndServices = 'Software & Services',
  TechnologyHardwareAndEquipment = 'Technology Hardware & Equipment',
  TelecommunicationServices = 'Telecommunication Services',
  Transportation = 'Transportation',
  Utilities = 'Utilities',
}

export enum GicsIndustry {
  AerospaceAndDefense = 'Aerospace & Defense',
  AirFreightAndLogistics = 'Air Freight & Logistics',
  AutomobileComponents = 'Automobile Components',
  Automobiles = 'Automobiles',
  Banks = 'Banks',
  Beverages = 'Beverages',
  Biotechnology = 'Biotechnology',
  BroadlineRetail = 'Broadline Retail',
  BuildingProducts = 'Building Products',
  CapitalMarkets = 'Capital Markets',
  Chemicals = 'Chemicals',
  CommercialServicesAndSupplies = 'Commercial Services & Supplies',
  CommunicationsEquipment = 'Communications Equipment',
  ConstructionAndEngineering = 'Construction & Engineering',
  ConstructionMaterials = 'Construction Materials',
  ConsumerFinance = 'Consumer Finance',
  ConsumerStaplesDistributionAndRetail = 'Consumer Staples Distribution & Retail',
  ContainersAndPackaging = 'Containers & Packaging',
  DiversifiedConsumerServices = 'Diversified Consumer Services',
  DiversifiedREITs = 'Diversified REITs',
  DiversifiedTelecommunicationServices = 'Diversified Telecommunication Services',
  Distributors = 'Distributors',
  ElectricUtilities = 'Electric Utilities',
  ElectricalEquipment = 'Electrical Equipment',
  ElectronicEquipmentInstrumentsAndComponents = 'Electronic Equipment, Instruments & Components',
  EnergyEquipmentAndServices = 'Energy Equipment & Services',
  Entertainment = 'Entertainment',
  FinancialServices = 'Financial Services',
  FoodProducts = 'Food Products',
  GasUtilities = 'Gas Utilities',
  GroundTransportation = 'Ground Transportation',
  HealthCareEquipmentAndSupplies = 'Health Care Equipment & Supplies',
  HealthCareProvidersAndServices = 'Health Care Providers & Services',
  HealthCareREITs = 'Health Care REITs',
  HealthCareTechnology = 'Health Care Technology',
  HotelAndResortREITs = 'Hotel & Resort REITs',
  HotelsRestaurantsAndLeisure = 'Hotels, Restaurants & Leisure',
  HouseholdDurables = 'Household Durables',
  HouseholdProducts = 'Household Products',
  ITServices = 'IT Services',
  IndependentPowerAndRenewableElectricityProducers = 'Independent Power and Renewable Electricity Producers',
  IndustrialConglomerates = 'Industrial Conglomerates',
  IndustrialREITs = 'Industrial REITs',
  Insurance = 'Insurance',
  InteractiveMediaAndServices = 'Interactive Media & Services',
  LeisureProducts = 'Leisure Products',
  LifeSciencesToolsAndServices = 'Life Sciences Tools & Services',
  Machinery = 'Machinery',
  MarineTransportation = 'Marine Transportation',
  Media = 'Media',
  MetalsAndMining = 'Metals & Mining',
  MortgageRealEstateInvestmentTrusts = 'Mortgage Real Estate Investment Trusts (REITs)',
  MultiUtilities = 'Multi-Utilities',
  OfficeREITs = 'Office REITs',
  OilGasAndConsumableFuels = 'Oil, Gas & Consumable Fuels',
  PaperAndForestProducts = 'Paper & Forest Products',
  PassengerAirlines = 'Passenger Airlines',
  PersonalCareProducts = 'Personal Care Products',
  Pharmaceuticals = 'Pharmaceuticals',
  ProfessionalServices = 'Professional Services',
  RealEstateManagementAndDevelopment = 'Real Estate Management & Development',
  ResidentialREITs = 'Residential REITs',
  RetailREITs = 'Retail REITs',
  SemiconductorsAndSemiconductorEquipment = 'Semiconductors & Semiconductor Equipment',
  Software = 'Software',
  SpecializedREITs = 'Specialized REITs',
  SpecialtyRetail = 'Specialty Retail',
  TechnologyHardwareStorageAndPeripherals = 'Technology Hardware, Storage & Peripherals',
  TextilesApparelAndLuxuryGoods = 'Textiles, Apparel & Luxury Goods',
  Tobacco = 'Tobacco',
  TradingCompaniesAndDistributors = 'Trading Companies & Distributors',
  TransportationInfrastructure = 'Transportation Infrastructure',
  WaterUtilities = 'Water Utilities',
  WirelessTelecommunicationServices = 'Wireless Telecommunication Services',
}

export enum SeniorityType {
  Senior = 'Senior',
  Subordinate = 'Subordinate',
  Hybrid = 'Hybrid',
}

export enum AccretionCompoundingMethod {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  SemiAnnually = 'Semi-Annually',
  Annual = 'Annual',
}

export enum BondFormType {
  Bearer = 'Bearer',
  BookEntry = 'Book Entry',
  Materialised = 'Materialised',
  PermanentGlobalBearer = 'Permanent Global Bearer',
  PermanentGlobalRegistered = 'Permanent Global Registered',
  Registered = 'Registered',
}

export enum CocoEventType {
  BailIn = 'Bail-in',
  BailInBRRD = 'Bail-In (BRRD)',
  BailInIRRD = 'Bail-In (IRRD)',
  BailInNonEU = 'Bail-In (Non-EU)',
  CapitalAdequacy = 'Capital Adequacy',
  NVCC = 'NVCC (Non-viability Contingent Capital)',
}

export enum CocoTriggerAction {
  EquityConversionOnly = 'Equity Conversion Only',
  EquityConversionAndOrWriteDown = 'Equity Conversion and/or Write Down',
  WriteDownOnly = 'Write Down Only',
  Unknown = 'Unknown',
}

export enum CocoTriggerLevelType {
  CET1Ratio = 'CET1 Ratio',
  GroupCET1Ratio = 'Group CET1 Ratio',
  IssuerCET1Ratio = 'Issuer CET1 Ratio',
  EquityRatioLevel = 'Equity Ratio Level',
  OtherTriggerLevel = 'Other Trigger Level',
  OtherTriggerRatio = 'Other Trigger Ratio',
}

export enum CocoTriggerType {
  Discretionary = 'Discretionary',
  Mechanical = 'Mechanical',
}

export enum CouponTypeMethod {
  FixedToFloat = 'Fixed to Float',
  FixedToFloatToFixed = 'Fixed to Float to Fixed',
  FloatToFixed = 'Float to Fixed',
  FloatToFixedToFloat = 'Float to Fixed to Float',
}

export enum CoveredBondCollateral {
  MortgageLoans = 'Mortgage Loans',
  PublicSectorLoans = 'Public Sector Loans',
  ShipLoans = 'Ship Loans',
  SecuredBond = 'Secured Bond',
  Other = 'Other',
}

export enum EmergingStatus {
  Developed = 'Developed',
  Emerging = 'Emerging',
}

export enum CreditQualityClass {
  InvestmentGrade = 'Investment grade',
  HighYield = 'High yield',
  NotRated = 'Not rated',
}

export enum FitchOutlook {
  Positive = 'positive',
  Stable = 'stable',
  Negative = 'negative',
}

export enum MoodysOutlook {
  Positive = 'positive',
  Stable = 'stable',
  Negative = 'negative',
  RatingUnderReview = 'rating under review',
  RatingWithdrawn = 'rating withdrawn',
}

export enum FloatResetFrequency {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  SemiAnnually = 'Semi-Annually',
  Annually = 'Annually',
  Custom = 'Custom',
}

export enum ImpactBondType {
  Green = 'Green',
  Social = 'Social',
  Sustainable = 'Sustainable',
  Blue = 'Blue',
}

export enum SinkFundRedemptionMethod {
  ProRata = 'Pro-Rata',
  Lottery = 'Lottery',
  InverseOrder = 'Inverse Order',
  NoIndicationInTheOfficialStatement = 'No Indication in the Official Statement',
}

export enum StripType {
  InterestOnly = 'Interest Only',
  PrincipalOnly = 'Principal Only',
  InterestAndPrincipal = 'Interest and Principal',
  ReceiptOnly = 'Receipt Only',
  StripPackage = 'Strip Package',
}

export enum SukukContractType {
  Ijarah = 'Ijarah',
  Murabaha = 'Murabaha',
  Mudaraba = 'Mudaraba',
  Musharaka = 'Musharaka',
  Wakalah = 'Wakalah',
  WakalaBilIstithmar = 'Wakala bil-Istithmar',
  Istithmar = 'Istithmar',
  IstisnaA = 'Istisna a',
  Salam = 'Salam',
  Tawarruq = 'Tawarruq',
  BaiBithamanAjil = 'Bai Bithaman Ajil',
  Hybrid = 'Hybrid',
  Unknown = 'Unknown',
}

export enum SukukType {
  Domestic = 'Domestic',
  International = 'International',
}

export enum TradingRestrictionsType {
  Rule144A = '144A',
  Rule144ARegD = '144A/Reg D',
  RegD = 'Reg D',
  RegulationS = 'Regulation S',
  AccreditedInvestors = 'Accredited Investors',
  PrivatePlacementNon144A = 'Private Placement(Non-144A)',
}

export enum GicsSubIndustry {
  Advertising = 'Advertising',
  AerospaceAndDefense = 'Aerospace & Defense',
  AgriculturalAndFarmMachinery = 'Agricultural & Farm Machinery',
  AgriculturalProductsAndServices = 'Agricultural Products & Services',
  AirFreightAndLogistics = 'Air Freight & Logistics',
  AirportServices = 'Airport Services',
  AlternativeCarriers = 'Alternative Carriers',
  Aluminum = 'Aluminum',
  ApparelRetail = 'Apparel Retail',
  ApparelAccessoriesAndLuxuryGoods = 'Apparel, Accessories & Luxury Goods',
  ApplicationSoftware = 'Application Software',
  AssetManagementAndCustodyBanks = 'Asset Management & Custody Banks',
  AutomobileManufacturers = 'Automobile Manufacturers',
  AutomotivePartsAndEquipment = 'Automotive Parts & Equipment',
  AutomotiveRetail = 'Automotive Retail',
  Biotechnology = 'Biotechnology',
  Brewers = 'Brewers',
  Broadcasting = 'Broadcasting',
  BroadlineRetail = 'Broadline Retail',
  BuildingProducts = 'Building Products',
  CableAndSatellite = 'Cable & Satellite',
  CargoGroundTransportation = 'Cargo Ground Transportation',
  CasinosAndGaming = 'Casinos & Gaming',
  CoalAndConsumableFuels = 'Coal & Consumable Fuels',
  CommercialAndResidentialMortgageFinance = 'Commercial & Residential Mortgage Finance',
  CommercialPrinting = 'Commercial Printing',
  CommodityChemicals = 'Commodity Chemicals',
  CommunicationsEquipment = 'Communications Equipment',
  ComputerAndElectronicsRetail = 'Computer & Electronics Retail',
  ConstructionAndEngineering = 'Construction & Engineering',
  ConstructionMachineryAndHeavyTransportationEquipment = 'Construction Machinery & Heavy Transportation Equipment',
  ConstructionMaterials = 'Construction Materials',
  ConsumerElectronics = 'Consumer Electronics',
  ConsumerFinance = 'Consumer Finance',
  ConsumerStaplesMerchandiseRetail = 'Consumer Staples Merchandise Retail',
  Copper = 'Copper',
  DataCenterREITs = 'Data Center REITs',
  DataProcessingAndOutsourcedServices = 'Data Processing & Outsourced Services',
  DistillersAndVintners = 'Distillers & Vintners',
  Distributors = 'Distributors',
  DiversifiedBanks = 'Diversified Banks',
  DiversifiedCapitalMarkets = 'Diversified Capital Markets',
  DiversifiedChemicals = 'Diversified Chemicals',
  DiversifiedFinancialServices = 'Diversified Financial Services',
  DiversifiedMetalsAndMining = 'Diversified Metals & Mining',
  DiversifiedREITs = 'Diversified REITs',
  DiversifiedRealEstateActivities = 'Diversified Real Estate Activities',
  DiversifiedSupportServices = 'Diversified Support Services',
  DrugRetail = 'Drug Retail',
  EducationServices = 'Education Services',
  ElectricUtilities = 'Electric Utilities',
  ElectricalComponentsAndEquipment = 'Electrical Components & Equipment',
  ElectronicComponents = 'Electronic Components',
  ElectronicEquipmentAndInstruments = 'Electronic Equipment & Instruments',
  ElectronicManufacturingServices = 'Electronic Manufacturing Services',
  EnvironmentalAndFacilitiesServices = 'Environmental & Facilities Services',
  FertilizersAndAgriculturalChemicals = 'Fertilizers & Agricultural Chemicals',
  FinancialExchangesAndData = 'Financial Exchanges & Data',
  FoodDistributors = 'Food Distributors',
  FoodRetail = 'Food Retail',
  Footwear = 'Footwear',
  ForestProducts = 'Forest Products',
  GasUtilities = 'Gas Utilities',
  Gold = 'Gold',
  HealthCareDistributors = 'Health Care Distributors',
  HealthCareEquipment = 'Health Care Equipment',
  HealthCareFacilities = 'Health Care Facilities',
  HealthCareREITs = 'Health Care REITs',
  HealthCareServices = 'Health Care Services',
  HealthCareSupplies = 'Health Care Supplies',
  HealthCareTechnology = 'Health Care Technology',
  HeavyElectricalEquipment = 'Heavy Electrical Equipment',
  HighwaysAndRailtracks = 'Highways & Railtracks',
  HomeFurnishings = 'Home Furnishings',
  HomeImprovementRetail = 'Home Improvement Retail',
  Homebuilding = 'Homebuilding',
  HomefurnishingRetail = 'Homefurnishing Retail',
  HotelAndResortREITs = 'Hotel & Resort REITs',
  HotelsResortsAndCruiseLines = 'Hotels, Resorts & Cruise Lines',
  HouseholdAppliances = 'Household Appliances',
  HouseholdProducts = 'Household Products',
  HousewaresAndSpecialties = 'Housewares & Specialties',
  HumanResourceAndEmploymentServices = 'Human Resource & Employment Services',
  ITConsultingAndOtherServices = 'IT Consulting & Other Services',
  IndependentPowerProducersAndEnergyTraders = 'Independent Power Producers & Energy Traders',
  IndustrialConglomerates = 'Industrial Conglomerates',
  IndustrialGases = 'Industrial Gases',
  IndustrialMachineryAndSuppliesAndComponents = 'Industrial Machinery & Supplies & Components',
  IndustrialREITs = 'Industrial REITs',
  InsuranceBrokers = 'Insurance Brokers',
  IntegratedOilAndGas = 'Integrated Oil & Gas',
  IntegratedTelecommunicationServices = 'Integrated Telecommunication Services',
  InteractiveHomeEntertainment = 'Interactive Home Entertainment',
  InteractiveMediaAndServices = 'Interactive Media & Services',
  InternetServicesAndInfrastructure = 'Internet Services & Infrastructure',
  InvestmentBankingAndBrokerage = 'Investment Banking & Brokerage',
  LeisureFacilities = 'Leisure Facilities',
  LeisureProducts = 'Leisure Products',
  LifeAndHealthInsurance = 'Life & Health Insurance',
  LifeSciencesToolsAndServices = 'Life Sciences Tools & Services',
  ManagedHealthCare = 'Managed Health Care',
  MarinePortsAndServices = 'Marine Ports & Services',
  MarineTransportation = 'Marine Transportation',
  MetalGlassAndPlasticContainers = 'Metal, Glass & Plastic Containers',
  MortgageREITs = 'Mortgage REITs',
  MotorcycleManufacturers = 'Motorcycle Manufacturers',
  MoviesAndEntertainment = 'Movies & Entertainment',
  MultiFamilyResidentialREITs = 'Multi-Family Residential REITs',
  MultiSectorHoldings = 'Multi-Sector Holdings',
  MultiUtilities = 'Multi-Utilities',
  MultiLineInsurance = 'Multi-line Insurance',
  OfficeREITs = 'Office REITs',
  OfficeServicesAndSupplies = 'Office Services & Supplies',
  OilAndGasDrilling = 'Oil & Gas Drilling',
  OilAndGasEquipmentAndServices = 'Oil & Gas Equipment & Services',
  OilAndGasExplorationAndProduction = 'Oil & Gas Exploration & Production',
  OilAndGasRefiningAndMarketing = 'Oil & Gas Refining & Marketing',
  OilAndGasStorageAndTransportation = 'Oil & Gas Storage & Transportation',
  OtherSpecializedREITs = 'Other Specialized REITs',
  OtherSpecialtyRetail = 'Other Specialty Retail',
  PackagedFoodsAndMeats = 'Packaged Foods & Meats',
  PaperAndPlasticPackagingProductsAndMaterials = 'Paper & Plastic Packaging Products & Materials',
  PaperProducts = 'Paper Products',
  PassengerAirlines = 'Passenger Airlines',
  PassengerGroundTransportation = 'Passenger Ground Transportation',
  PersonalCareProducts = 'Personal Care Products',
  Pharmaceuticals = 'Pharmaceuticals',
  PreciousMetalsAndMinerals = 'Precious Metals & Minerals',
  PropertyAndCasualtyInsurance = 'Property & Casualty Insurance',
  Publishing = 'Publishing',
  RailTransportation = 'Rail Transportation',
  RealEstateDevelopment = 'Real Estate Development',
  RealEstateOperatingCompanies = 'Real Estate Operating Companies',
  RealEstateServices = 'Real Estate Services',
  RegionalBanks = 'Regional Banks',
  Reinsurance = 'Reinsurance',
  RenewableElectricity = 'Renewable Electricity',
  ResearchAndConsultingServices = 'Research & Consulting Services',
  Restaurants = 'Restaurants',
  RetailREITs = 'Retail REITs',
  SecurityAndAlarmServices = 'Security & Alarm Services',
  SelfStorageREITs = 'Self-Storage REITs',
  SemiconductorMaterialsAndEquipment = 'Semiconductor Materials & Equipment',
  Semiconductors = 'Semiconductors',
  Silver = 'Silver',
  SingleFamilyResidentialREITs = 'Single-Family Residential REITs',
  SoftDrinksAndNonAlcoholicBeverages = 'Soft Drinks & Non-alcoholic Beverages',
  SpecializedConsumerServices = 'Specialized Consumer Services',
  SpecializedFinance = 'Specialized Finance',
  SpecialtyChemicals = 'Specialty Chemicals',
  Steel = 'Steel',
  SystemsSoftware = 'Systems Software',
  TechnologyDistributors = 'Technology Distributors',
  TechnologyHardwareStorageAndPeripherals = 'Technology Hardware, Storage & Peripherals',
  TelecomTowerREITs = 'Telecom Tower REITs',
  Textiles = 'Textiles',
  TimberREITs = 'Timber REITs',
  TiresAndRubber = 'Tires & Rubber',
  Tobacco = 'Tobacco',
  TradingCompaniesAndDistributors = 'Trading Companies & Distributors',
  TransactionAndPaymentProcessingServices = 'Transaction & Payment Processing Services',
  WaterUtilities = 'Water Utilities',
  WirelessTelecommunicationServices = 'Wireless Telecommunication Services',
}
